import type {} from '@mui/lab/themeAugmentation';
import { alpha, createTheme, responsiveFontSizes } from '@mui/material/styles';
import { blue, green } from '@mui/material/colors';

const ourGreen = '#08f228';
const ourGreenDark = '#23b537';
const ourRed = '#f00';
const quietRedText = '#ffb3b3';
const lightGrey = '#333';

const createQuietButtonStyles = (mainColor: string, textColor: string) => ({
  color: textColor,
  backgroundColor: alpha(mainColor, 0.14),
  border: `1px solid ${alpha(mainColor, 0.34)}`,
  '&:hover': {
    backgroundColor: alpha(mainColor, 0.22),
    borderColor: alpha(mainColor, 0.48),
  },
  '&.Mui-focusVisible': {
    boxShadow: `0 0 0 3px ${alpha(mainColor, 0.2)}`,
  },
  '&.Mui-disabled': {
    color: alpha(textColor, 0.55),
    backgroundColor: alpha(mainColor, 0.08),
    borderColor: alpha(mainColor, 0.16),
  },
});

// REQ-011: keep lower-emphasis primary and destructive buttons on one shared quiet variant.
const quietButtonVariants = [
  {
    props: { color: 'primary' as const, variant: 'quiet' as const },
    style: createQuietButtonStyles(ourGreen, green[200]),
  },
  {
    props: { color: 'secondary' as const, variant: 'quiet' as const },
    style: createQuietButtonStyles(ourRed, quietRedText),
  },
];

export const createAppTheme = () => {
  let theme = createTheme({
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            border: '1px solid green',
          },
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            backgroundColor: lightGrey,
          },
        },
      },
      MuiButton: {
        variants: quietButtonVariants,
      },
      MuiLoadingButton: {
        variants: quietButtonVariants,
      },
    },
    palette: {
      mode: 'dark',
      primary: {
        main: ourGreen,
        dark: ourGreenDark,
        light: green[200],
      },
      secondary: {
        main: ourRed,
        dark: '#000',
        light: '#fff',
      },
      info: {
        main: blue[500],
        dark: blue[700],
        light: blue[300],
      },
      text: {
        primary: 'rgba(255, 255, 255, 0.87)',
      },
    },
    typography: {
      fontFamily: 'Monda, sans-serif',
      h1: { fontFamily: 'Monda, sans-serif' },
      h2: { fontFamily: 'Monda, sans-serif' },
      h3: { fontFamily: 'Monda, sans-serif' },
      h4: { fontFamily: 'Monda, sans-serif' },
      h5: { fontFamily: 'Monda, sans-serif' },
      h6: { fontFamily: 'Monda, sans-serif' },
      body1: {
        fontFamily: 'Arial, sans-serif',
        fontWeight: 300,
        '@media (min-width:600px)': {
          fontSize: '20px',
          lineHeight: '32px',
        },
        '@media (max-width:599px)': {
          fontSize: '18px',
          lineHeight: '29px',
        },
      },
      body2: {
        fontFamily: 'Arial, sans-serif',
        fontWeight: 'normal',
        '@media (min-width:600px)': {
          fontSize: '13px',
        },
        '@media (max-width:599px)': {
          fontSize: '11px',
        },
      },
      bigPrimary_body2: {
        fontFamily: 'Arial, sans-serif',
        fontWeight: 'bold',
        '@media (min-width:600px)': {
          fontSize: '15px',
        },
        '@media (max-width:599px)': {
          fontSize: '13px',
        },
        color: ourGreen,
      },
      bigPrimary: {
        '@media (min-width:600px)': {
          fontSize: '28px',
        },
        '@media (max-width:599px)': {
          fontSize: '18px',
        },
        color: ourGreen,
        fontWeight: 700,
        fontFamily: 'Monda, sans-serif',
      },
      hugePrimary: {
        fontSize: '70px',
        color: ourGreen,
        fontWeight: 700,
        fontFamily: 'Monda, sans-serif',
      },
      cardSubtitle: {
        '@media (min-width:600px)': {
          fontSize: '28px',
        },
        '@media (max-width:599px)': {
          fontSize: '18px',
        },
        fontWeight: 700,
        fontFamily: 'Arial, sans-serif',
      },
    },
  });

  theme = responsiveFontSizes(theme);
  return theme;
};
